// src/components/Financials/api.js - Reports dashboard API (AccessManager pattern, same as Diary)

import accessManager from '../Auth/Access'; // Import the AccessManager singleton
import BASE_URL from '../config';

// Builds "?a=1&b=2" from an object, skipping empty values.
const buildQuery = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const qs = queryParams.toString();
  return qs ? `?${qs}` : '';
};

// Some request helpers wrap the JSON as { data: ... } - accept both shapes.
const unwrap = (response) =>
  response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)
    ? response.data
    : response;

// Fail loudly when an endpoint answers with something unexpected (e.g. it
// isn't deployed yet and an HTML/404 page comes back), instead of the page
// silently showing empty tables.
const expectKey = (response, key, endpoint) => {
  const data = unwrap(response);
  if (!data || data[key] === undefined) {
    throw new Error(
      data?.error || `Unexpected response from ${endpoint} - check that the endpoint is deployed.`
    );
  }
  return data;
};

const requireAuth = () => {
  if (!accessManager.isAuthenticated()) {
    throw new Error('Authentication required');
  }
};

// ---------------------------------------------------------------------
// Excel downloads need the raw file, not parsed JSON, so they use fetch
// directly with the same token AccessManager uses.
//
// If your AccessManager exposes the token under a different name, change
// getAuthToken() - it is the only place that reads it.
// ---------------------------------------------------------------------
const getAuthToken = () =>
  (typeof accessManager.getAccessToken === 'function' && accessManager.getAccessToken()) ||
  (typeof accessManager.getToken === 'function' && accessManager.getToken()) ||
  accessManager.accessToken ||
  accessManager.token ||
  localStorage.getItem('access_token') ||
  localStorage.getItem('accessToken');

const downloadFile = async (endpoint, fallbackName) => {
  requireAuth();

  let response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
  } catch (err) {
    // The browser gives no detail here (net::ERR_FAILED / "Failed to fetch").
    // For reports this almost always means the server stopped the request
    // before the file was ready - e.g. a long range hit the server timeout.
    throw new Error(
      'The server did not finish the file in time. Try again, or download a shorter range ' +
      '(for example single months) while the server timeout is raised.'
    );
  }

  if (response.status === 401) {
    throw new Error('Authentication required (401)');
  }
  if (!response.ok) {
    let message = `Download failed (${response.status})`;
    try {
      const data = await response.json();
      message = data.error || message;
    } catch (e) {
      // not JSON - keep the status message
    }
    throw new Error(message);
  }

  // Server filename when readable (needs CORS_EXPOSE_HEADERS =
  // ['Content-Disposition'] on the backend), otherwise the fallback.
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};

// ---------------------------------------------------------------------
// Dashboard data (JSON)
// ---------------------------------------------------------------------
export const dashboardAPI = {
  // Company revenue, expenses and savings for a date range, with a
  // per-month breakdown and the top groups.
  // source: 'pa' (performance analysis, default) or 'alloc' (allocations).
  // basis:  'cash' (cash basis, default) or 'accrual' - how loan revenue is worked out.
  getRevenue: async (startDate, endDate, source = 'pa', basis = 'cash') => {
    requireAuth();
    const endpoint = '/reports/dashboard/revenue/';
    const response = await accessManager.request(
      `${endpoint}${buildQuery({ start: startDate, end: endDate, source, basis })}`,
      { method: 'GET' }
    );
    return expectKey(response, 'months', endpoint);
  },

  // Expected installments / minimum advance revenue for one month.
  // Heavier than the others, so the dashboard calls it month by month.
  getMonthExpected: async (year, month) => {
    requireAuth();
    const endpoint = '/reports/dashboard/expected/';
    const response = await accessManager.request(`${endpoint}${buildQuery({ year, month })}`, {
      method: 'GET',
    });
    return expectKey(response, 'expected_inst', endpoint);
  },
};

// ---------------------------------------------------------------------
// Portfolio summaries (JSON previews of the Excel reports)
// ---------------------------------------------------------------------
export const portfolioAPI = {
  // Loan portfolio summary. asOf: 'YYYY-MM-DD' or empty for today.
  getLoanSummary: async (asOf) => {
    requireAuth();
    const endpoint = '/reports/loan-portfolio/';
    const response = await accessManager.request(
      `${endpoint}${buildQuery({ preview: 'true', as_of: asOf })}`,
      { method: 'GET' }
    );
    return expectKey(response, 'summary', endpoint);
  },

  // Advance portfolio summary (regular and school fees, as of today).
  getAdvanceSummary: async () => {
    requireAuth();
    const endpoint = '/reports/advance-portfolio/';
    const response = await accessManager.request(`${endpoint}${buildQuery({ preview: 'true' })}`, {
      method: 'GET',
    });
    return expectKey(response, 'summary', endpoint);
  },
};

// ---------------------------------------------------------------------
// Excel downloads
// ---------------------------------------------------------------------
export const reportFilesAPI = {
  // Revenue workbook for any range: one month, a year, or a custom range.
  // The server names it revenue_2026-07.xlsx / revenue_2026.xlsx /
  // revenue_<start>_to_<end>.xlsx.
  downloadRevenue: async (startDate, endDate, basis = 'cash') =>
    downloadFile(
      `/reports/monthly-revenue/${buildQuery({ start: startDate, end: endDate, basis })}`,
      `revenue_${startDate}_to_${endDate}_${basis}.xlsx`
    ),

  downloadMonthlyRevenue: async (year, month, basis = 'cash') =>
    downloadFile(
      `/reports/monthly-revenue/${buildQuery({ year, month, basis })}`,
      `revenue_${year}-${String(month).padStart(2, '0')}_${basis}.xlsx`
    ),

  downloadLoanPortfolio: async (asOf) =>
    downloadFile(
      `/reports/loan-portfolio/${buildQuery({ as_of: asOf })}`,
      `loan_portfolio_${asOf || 'today'}.xlsx`
    ),

  downloadAdvancePortfolio: async () =>
    downloadFile('/reports/advance-portfolio/', 'advance_portfolio.xlsx'),
};

// Combined API object for easy importing
export const reportsAPI = {
  dashboard: dashboardAPI,
  portfolio: portfolioAPI,
  files: reportFilesAPI,
};

// Named functions used by ReportsDashboard.js
export const fetchRevenueDashboard = dashboardAPI.getRevenue;
export const fetchMonthExpected = dashboardAPI.getMonthExpected;
export const fetchLoanPortfolioSummary = portfolioAPI.getLoanSummary;
export const fetchAdvancePortfolioSummary = portfolioAPI.getAdvanceSummary;
export const downloadRevenueExcel = reportFilesAPI.downloadRevenue;
export const downloadMonthlyRevenueExcel = reportFilesAPI.downloadMonthlyRevenue;
export const downloadLoanPortfolioExcel = reportFilesAPI.downloadLoanPortfolio;
export const downloadAdvancePortfolioExcel = reportFilesAPI.downloadAdvancePortfolio;

// Export the access manager for direct use if needed
export { accessManager };

export default reportsAPI;